/**
 * Route Utilities - Helper functions for permission-based route filtering
 */

/**
 * Check if a user has access to a specific route based on permission requirements
 * 
 * @param {Object|Array|null} requiredPermission - Permission requirement(s)
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {boolean} - True if user has access
 */
export const hasRouteAccess = (requiredPermission, canCheck) => {
  // No permission required = public route (accessible to all authenticated users)
  if (!requiredPermission) {
    return true;
  }

  // Single permission check
  if (!Array.isArray(requiredPermission)) {
    const { section, level = 'read', action } = requiredPermission;
    return action 
      ? canCheck.section(section, { action })
      : canCheck.section(section, { level });
  }

  // Multiple permissions - user needs ANY of them
  return canCheck.any(requiredPermission.map(p => ({ 
    section: p.section, 
    level: p.level || 'read' 
  })));
};

/**
 * Filter routes based on user permissions
 * 
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {Array} - Filtered array of accessible routes
 */
export const filterAccessibleRoutes = (routes, canCheck) => {
  return routes.filter(route => hasRouteAccess(route.permission, canCheck));
};

/**
 * Get accessible navigation items based on routes
 * Useful for filtering drawer/menu items
 * 
 * @param {Array} navItems - Navigation items with route paths
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {Array} - Filtered navigation items
 */
export const filterAccessibleNavItems = (navItems, routes, canCheck) => {
  const accessiblePaths = new Set(
    filterAccessibleRoutes(routes, canCheck).map(r => r.path)
  );

  return navItems.filter(item => {
    // Always keep dividers
    if (item.divider) return true;
    
    // If item has no route, it might be a section header or parent item
    if (!item.route) return true;
    
    // Extract base path (without params) for comparison
    const basePath = item.route.split('/')[1] || item.route;
    
    // Check if any accessible route matches this nav item
    return Array.from(accessiblePaths).some(path => {
      const routeBasePath = path.split('/')[0];
      return routeBasePath === basePath;
    });
  });
};

/**
 * Check if a specific path is accessible
 * 
 * @param {string} path - Route path to check
 * @param {Array} routes - Array of route configurations
 * @param {Function} canCheck - The can checking function from useCan hook
 * @returns {boolean} - True if path is accessible
 */
export const isPathAccessible = (path, routes, canCheck) => {
  // Remove leading slash and params for matching
  const cleanPath = path.replace(/^\//, '').split('?')[0].split('/')[0];
  
  const matchingRoute = routes.find(route => {
    const routePath = route.path.split('/')[0];
    return routePath === cleanPath;
  });

  if (!matchingRoute) {
    // Route doesn't exist in configuration
    return false;
  }

  return hasRouteAccess(matchingRoute.permission, canCheck);
};
